import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Stack,
  Typography,
  TextField,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  ListItem,
  Chip,
  Skeleton,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate } from "react-router-dom";
import { collection, getDocs, orderBy, query, limit, where } from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { db } from "../firebase-config";

export default function SheetList() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);

  const [mode, setMode] = useState("public"); // public | mine
  const [qText, setQText] = useState("");
  const [loading, setLoading] = useState(true);
  const [sheets, setSheets] = useState([]);

  useEffect(() => {
    const auth = getAuth();
    return onAuthStateChanged(auth, (u) => setUser(u || null));
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function run() {
      setLoading(true);
      try {
        const baseRef = collection(db, "sheets");

        let qq;
        if (mode === "mine" && user?.uid) {
          qq = query(
            baseRef,
            where("authorUid", "==", user.uid),
            orderBy("updatedAt", "desc"),
            limit(200)
          );
        } else {
          qq = query(
            baseRef,
            where("visibility", "==", "public"),
            where("status", "==", "published"),
            orderBy("updatedAt", "desc"),
            limit(200)
          );
        }

        const snap = await getDocs(qq);
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        if (!cancelled) setSheets(rows);
      } catch (e) {
        console.error(e);
        if (!cancelled) setSheets([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    run();
    return () => {
      cancelled = true;
    };
  }, [mode, user?.uid]);

  const filtered = useMemo(() => {
    const t = qText.trim().toLowerCase();
    if (!t) return sheets;
    return sheets.filter((s) => {
      const hay = `${s.name || ""} ${s.version ?? ""} ${s.authorName || ""}`.toLowerCase();
      return hay.includes(t);
    });
  }, [sheets, qText]);

  return (
    <Box sx={{ maxWidth: 1000, margin: "0 auto" }}>
      <Box sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" flexWrap="wrap">
            <Typography variant="h5">Sheets</Typography>

            <ToggleButtonGroup size="small" value={mode} exclusive onChange={(e, v) => v && setMode(v)}>
              <ToggleButton value="public">Public</ToggleButton>
              <ToggleButton value="mine" disabled={!user}>
                Mine
              </ToggleButton>
            </ToggleButtonGroup>
          </Stack>

          <TextField
            value={qText}
            onChange={(e) => setQText(e.target.value)}
            placeholder="Search sheets…"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />

          {loading ? (
            <Stack spacing={1}>
              <Skeleton height={50} />
              <Skeleton height={50} />
              <Skeleton height={50} />
            </Stack>
          ) : (
            <List disablePadding>
              {filtered.map((s) => (
                <ListItemButton key={s.id} onClick={() => navigate(`/sheet/${s.id}`)}>
                  <ListItemText
                    primary={s.name || s.id}
                    secondary={`v${s.version || "0"} • ${s.authorName || s.authorUid || "unknown"}`}
                  />
                  <Chip size="small" label={s.visibility || "private"} />
                </ListItemButton>
              ))}

              {!filtered.length && (
                <ListItem>
                  <Typography variant="body2" sx={{ opacity: 0.7 }}>
                    No sheets found.
                  </Typography>
                </ListItem>
              )}
            </List>
          )}
        </Stack>
      </Box>
    </Box>
  );
}
